import { useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Simple debounce function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const context = this;
    const later = () => {
      timeout = null;
      func.apply(context, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const SESSION_STORAGE_PREFIX = "scrollRestoration_";
const SCROLL_RESTORATION_TIMEOUT_MS = 200; // Time for content to load before restoring
const SCROLL_SAVE_DEBOUNCE_MS = 100;     // How often to update the live scroll position ref during scroll

function getLocationKey(location) {
  return `${location.pathname}${location.search}`;
}

function getScrollPositionFromSession(key) {
  if (!key) return undefined;
  try {
    const item = sessionStorage.getItem(SESSION_STORAGE_PREFIX + key);
    if (item) {
      const parsed = parseFloat(item);
      return isNaN(parsed) ? undefined : parsed;
    }
  } catch (e) {
    console.warn("ScrollRestorer: Could not read from sessionStorage.", e);
  }
  return undefined;
}

function saveScrollPositionToSession(key, position) {
  if (!key) return;
  try {
    if (typeof position === 'number') {
      sessionStorage.setItem(SESSION_STORAGE_PREFIX + key, String(position));
    } else {
      console.warn(`ScrollRestorer: Attempted to save invalid scroll position for key ${key}: ${position}`);
    }
  } catch (e) {
    console.warn("ScrollRestorer: Could not write to sessionStorage.", e);
  }
}

export function ScrollRestorer() {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Ref to store the current page's key. Updated when location changes.
  const pageKeyRef = useRef(getLocationKey(location));
  // Ref to store the last known scrollY for the current pageKey.
  const lastScrollYRef = useRef(window.scrollY);
  const firstRenderRef = useRef(true);

  // Debounced function to update lastScrollYRef.
  // useCallback ensures the same debounced function instance is used across re-renders
  // unless its dependencies change (none here).
  const debouncedUpdateLastScrollY = useCallback(debounce(() => {
    // Only update if the key hasn't changed underneath us (e.g. rapid navigation)
    // This check might be overly cautious but helps ensure we're updating scroll for the intended page.
    if (pageKeyRef.current === getLocationKey(location)) {
        lastScrollYRef.current = window.scrollY;
    }
  }, SCROLL_SAVE_DEBOUNCE_MS), [location]); // location dependency for getLocationKey check

  // Effect 1: Setup scroll listener and save on leave
  useEffect(() => {
    const currentKey = getLocationKey(location);
    pageKeyRef.current = currentKey; // Update current page key

    // Set initial scrollY for this page instance
    // If it's not a POP, it's a new page, scrollY should be 0 unless restored later
    // If it is a POP, restoration effect will handle it, but this sets a baseline
    if (navigationType !== 'POP' && firstRenderRef.current) { // Only for initial load of new pages
        lastScrollYRef.current = 0;
    } else {
        // For POP or subsequent renders on the same page, trust existing window.scrollY or restoration
        lastScrollYRef.current = window.scrollY;
    }

    window.addEventListener('scroll', debouncedUpdateLastScrollY);

    // Cleanup: This is CRITICAL. Runs when location changes (leaving this page) or component unmounts.
    return () => {
      window.removeEventListener('scroll', debouncedUpdateLastScrollY);
      // `currentKey` here is the key of the page this effect instance was for (the page we are LEAVING).
      // `lastScrollYRef.current` should hold its final scroll position.
      saveScrollPositionToSession(currentKey, lastScrollYRef.current);
    };
  }, [location, navigationType, debouncedUpdateLastScrollY]); // Add navigationType because it affects initial lastScrollYRef logic

  // Effect 2: Initial browser history setup and 'beforeunload'
  useEffect(() => {
    if (window.history.scrollRestoration) {
      window.history.scrollRestoration = "manual";
    }
    const handleBeforeUnload = () => {
      // Use pageKeyRef.current as it should reflect the truly current page at unload time.
      saveScrollPositionToSession(pageKeyRef.current, lastScrollYRef.current);
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []); // Runs once

  // Effect 3: Scroll restoration / scrolling to top
  useEffect(() => {
    const currentLocKey = getLocationKey(location);

    let timerId;

    if (firstRenderRef.current) {
      firstRenderRef.current = false; // Mark initial render as handled
      const savedY = getScrollPositionFromSession(currentLocKey);
      if (typeof savedY === "number") {
        timerId = setTimeout(() => {
          window.scrollTo(0, savedY);
          lastScrollYRef.current = savedY; // Sync our ref after restoration
        }, SCROLL_RESTORATION_TIMEOUT_MS);
      } else if (navigationType !== 'POP') { // Scroll to top if new page and no saved pos
        window.scrollTo(0, 0);
        lastScrollYRef.current = 0;
      }
    } else if (navigationType === "POP") {
      const savedY = getScrollPositionFromSession(currentLocKey);
      if (typeof savedY === "number") {
        timerId = setTimeout(() => {
          window.scrollTo(0, savedY);
          lastScrollYRef.current = savedY; // Sync our ref
        }, SCROLL_RESTORATION_TIMEOUT_MS);
      } else { // Fallback for POP if no position
        window.scrollTo(0, 0);
        lastScrollYRef.current = 0;
      }
    } else { // PUSH or REPLACE (subsequent navigations)
      window.scrollTo(0, 0);
      lastScrollYRef.current = 0;
    }

    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [location, navigationType]);

  return null;
}